import { supabase } from './supabase';
import type { Folder, Project, Version, FileItem, ShareLink } from './types';

// All reads are scoped by owner_id so an admin's own view (and admin "browse as
// user") stay distinct even though RLS would let an admin read every row.

export async function getFolder(id: string): Promise<Folder | null> {
  const { data, error } = await supabase.from('folders').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return data as Folder | null;
}

export async function getProject(id: string): Promise<Project | null> {
  const { data, error } = await supabase.from('projects').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return data as Project | null;
}

export async function listFolders(ownerId: string, parentId: string | null): Promise<Folder[]> {
  let q = supabase.from('folders').select('*').eq('owner_id', ownerId).order('name');
  q = parentId === null ? q.is('parent_id', null) : q.eq('parent_id', parentId);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as Folder[];
}

export async function listProjects(ownerId: string, folderId: string | null): Promise<Project[]> {
  let q = supabase.from('projects').select('*').eq('owner_id', ownerId).order('name');
  q = folderId === null ? q.is('folder_id', null) : q.eq('folder_id', folderId);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as Project[];
}

export async function listFilesInFolder(ownerId: string, folderId: string | null): Promise<FileItem[]> {
  let q = supabase
    .from('files')
    .select('*')
    .eq('owner_id', ownerId)
    .is('version_id', null)
    .order('name');
  q = folderId === null ? q.is('folder_id', null) : q.eq('folder_id', folderId);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as FileItem[];
}

export async function listVersions(projectId: string): Promise<Version[]> {
  const { data, error } = await supabase
    .from('versions')
    .select('*')
    .eq('project_id', projectId)
    .order('position', { ascending: true });
  if (error) throw error;
  return (data ?? []) as Version[];
}

export async function listFilesInVersion(versionId: string): Promise<FileItem[]> {
  const { data, error } = await supabase
    .from('files')
    .select('*')
    .eq('version_id', versionId)
    .order('name');
  if (error) throw error;
  return (data ?? []) as FileItem[];
}

export async function listSharesForFile(fileId: string): Promise<ShareLink[]> {
  const { data, error } = await supabase
    .from('share_links')
    .select('*')
    .eq('file_id', fileId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as ShareLink[];
}
